import React, { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import {
  BodyText,
  getFormattedExpirationDate,
  ModalDialogue,
  ModalHeadline,
  ModalRow,
  ModalSubhead,
  RewardsRail,
  TextLink,
  TextLinkify,
  theme,
  useContainerSpacing,
  useResponsive,
} from '@sg/garnish';

import { useChallengesAndRewards } from '@order/ChallengesAndRewards';
import type { ChallengeReward } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { useChallengesTelemetry } from '../../../../ChallengesScreen.telemetry';
import { IllusRewards } from '../../assets';
import { ChallengesScreenInfoPanel } from '../ChallengesScreenInfoPanel';

// ────────────────────────────────────────────────────────────────────────────────

export const ChallengesScreenRewards = () => (
  <View style={styles.wrapper}>
    <ChallengesScreenRewardsContent />
  </View>
);

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const ChallengesScreenRewardsContent = () => {
  const {
    challengeRewards,
    isFetchingChallengeRewards,
    challengeRewardsError,
  } = useChallengesAndRewards();

  const isLoadingChallengeRewards =
    challengeRewards === undefined || isFetchingChallengeRewards;
  const hasAvailableRewards =
    Array.isArray(challengeRewards) && challengeRewards.length > 0;
  const shouldRenderRewardsRail =
    isLoadingChallengeRewards || hasAvailableRewards;

  // ─────────────────────────────────────────────────────────────────

  if (challengeRewardsError) return <ChallengesScreenRewardsError />;

  if (shouldRenderRewardsRail) return <ChallengesScreenRewardsRail />;

  return <ChallengesScreenNoRewards />;
};

const ChallengesScreenRewardsRail = () => {
  const { challengeRewards, isFetchingChallengeRewards } =
    useChallengesAndRewards();
  const { t } = useLocalizationContext();
  const { minWidth } = useResponsive();

  // ─────────────────────────────────────────────────────────────────

  const [activeRewardId, setActiveRewardId] = useState('');

  const resetActiveRewardId = useCallback(() => {
    setActiveRewardId('');
  }, []);

  const activeReward = challengeRewards?.find(
    (reward) => reward.id === activeRewardId,
  );

  const containerSpacing = useContainerSpacing();

  // ─────────────────────────────────────────────────────────────────

  const isLoadingChallengeRewards =
    challengeRewards === undefined || isFetchingChallengeRewards;

  return (
    <View>
      <RewardsRail
        rewards={challengeRewards}
        isLoading={isLoadingChallengeRewards}
        headerText={t('challenges.rewards.header')}
        footerText={t('challenges.rewards.railFooterText')}
        deadlinePrefixText={t('challenges.rewards.railDeadlinePrefixText')}
        headerVariation={minWidth.tablet ? 'display' : 'body'}
        outerOffset={-containerSpacing}
        onPress={setActiveRewardId}
        withoutHeaderBorder={minWidth.tablet}
      />

      <ChallengesScreenRewardModal
        reward={activeReward}
        onDismiss={resetActiveRewardId}
      />
    </View>
  );
};

const ChallengesScreenRewardModal = (
  props: ChallengesScreenRewardModalProps,
) => {
  const { reward, onDismiss } = props;
  const { t } = useLocalizationContext();
  const {
    handleRewardDetailsTelemetry,
    handleRewardClosedTelemetry,
    handleRewardLinkTelemetry,
  } = useChallengesTelemetry();

  const [isVisible, setIsVisible] = useState(Boolean(reward));

  const rewardName = reward?.name ?? '';
  const terms = reward?.terms ?? t('challenges.rewards.modal.terms');
  const rewardExpirationDate =
    getFormattedExpirationDate(reward?.expiresAt) ?? '';

  // ─── HELPERS ─────────────────────────────────────────────────────

  const hideModal = useCallback(() => {
    setIsVisible(false);
    handleRewardClosedTelemetry();
  }, [handleRewardClosedTelemetry]);

  // ─── EFFECTS ─────────────────────────────────────────────────────

  useEffect(() => {
    setIsVisible(Boolean(reward));

    if (reward) handleRewardDetailsTelemetry(reward.id);
  }, [reward, handleRewardDetailsTelemetry]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      onRequestClose={hideModal}
      onDismissed={onDismiss}
      visible={isVisible}
      size="x-small"
      testID="challenges.rewards.modal"
    >
      <ModalHeadline
        style={styles.rewardModalHeadline}
        testID="challenges.rewards.modal.headline"
      >
        {rewardName}
      </ModalHeadline>

      <ModalSubhead
        style={styles.rewardModalSubheader}
        testID="challenges.rewards.modal.subhead"
      >
        {rewardExpirationDate}
      </ModalSubhead>

      <ScrollView>
        <ModalRow style={styles.rewardModalTextWrapper}>
          <BodyText size={3} style={styles.rewardModalText}>
            {t('challenges.rewards.modal.text')}
          </BodyText>

          <BodyText size={3} style={styles.rewardModalText}>
            <TextLinkify>{terms}</TextLinkify>
          </BodyText>

          <BodyText size={3}>
            {t('general.see')}
            <Text> </Text>
            <TextLink
              href={REWARDS_TERMS_LINK}
              onPress={handleRewardLinkTelemetry}
            >
              {REWARDS_TERMS_LINK}
              <Text>.</Text>
            </TextLink>
          </BodyText>
        </ModalRow>
      </ScrollView>
    </ModalDialogue>
  );
};

const ChallengesScreenNoRewards = () => {
  const { t } = useLocalizationContext();

  return (
    <ChallengesScreenInfoPanel
      heading={t('challenges.rewards.header')}
      text={t('challenges.rewards.noRewardsText')}
      illustrationSource={IllusRewards}
      testID="challenges.rewards.noRewards"
    />
  );
};

const ChallengesScreenRewardsError = () => {
  const { t } = useLocalizationContext();

  return (
    <ChallengesScreenInfoPanel
      heading={t('challenges.rewards.header')}
      text={t('challenges.rewards.error.network-error')}
      illustrationSource={IllusRewards}
    />
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    paddingTop: theme.spacing.small,
    paddingBottom: theme.spacing.xlarge,
  },
  rewardModalHeadline: {
    paddingBottom: theme.spacing.small,
  },
  rewardModalSubheader: {
    paddingBottom: theme.spacing.small,
  },
  rewardModalTextWrapper: {
    paddingBottom: theme.spacing.large,
  },
  rewardModalText: {
    paddingBottom: theme.spacing.medium,
  },
});

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const REWARDS_TERMS_LINK = 'https://www.sweetgreen.com/sweet-rewards';

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ChallengesScreenRewardModalProps = {
  readonly reward: ChallengeReward | undefined;
  readonly onDismiss: () => void;
};
